import React, { useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { NotificationItem, NotificationData, NotificationType } from './NotificationItem';
import { colorTokens } from '@/common/styles/colorTokens';
import { 
  Bell, 
  CheckCheck, 
  Loader2,
  ClipboardList,
  RefreshCw,
  MessageCircle,
  Search
} from 'lucide-react';

export type NotificationTheme = 'startup' | 'investor';

type TabType = 'TASK' | 'NOTIFICATION' | 'READONLY';

/** 탭별 미읽음 개수 */
export interface TabCounts {
  taskCount: number;
  notificationCount: number;
  readonlyCount: number;
}

export interface NotificationListProps {
  /** 현재 탭의 알림 목록 */
  notifications: NotificationData[];
  /** 각 탭별 미읽음 개수 */
  tabCounts: TabCounts;
  /** 현재 선택된 탭 */
  currentTab: NotificationType;
  /** 로딩 상태 */
  isLoading: boolean;
  /** 더 불러올 데이터가 있는지 */
  hasMore: boolean;
  /** 검색 키워드 */
  searchKeyword: string;
  /** 모두 읽음 처리 */
  onReadAll: () => void;
  /** 개별 알림 읽음 처리 */
  onRead: (notification: NotificationData) => void;
  /** 탭 변경 핸들러 */
  onTabChange: (type: NotificationType) => void;
  /** 더 불러오기 핸들러 (서버 페이지네이션) */
  onLoadMore: () => void;
  /** 검색어 변경 핸들러 */
  onSearchChange: (keyword: string) => void;
  /** 테마 (startup: 초록, investor: 파랑) */
  theme?: NotificationTheme;
}

const TAB_CONFIG: Record<TabType, { label: string; icon: React.ElementType }> = {
  TASK: { label: '할일', icon: ClipboardList },
  NOTIFICATION: { label: '업데이트', icon: RefreshCw },
  READONLY: { label: '메세지', icon: MessageCircle },
};

export const NotificationList: React.FC<NotificationListProps> = ({ 
  notifications,
  tabCounts,
  currentTab,
  isLoading,
  hasMore,
  searchKeyword,
  onReadAll, 
  onRead,
  onTabChange,
  onLoadMore,
  onSearchChange,
  theme = 'startup'
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // 전체 미읽음 개수
  const totalUnreadCount = tabCounts.taskCount + tabCounts.notificationCount + tabCounts.readonlyCount;

  // 탭별 미읽음 개수
  const getTabCount = (tab: TabType): number => {
    switch (tab) {
      case 'TASK': return tabCounts.taskCount;
      case 'NOTIFICATION': return tabCounts.notificationCount;
      case 'READONLY': return tabCounts.readonlyCount;
    }
  };

  // 탭 변경 시 스크롤 초기화
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentTab]);

  // 스크롤 이벤트 핸들러 (서버 페이지네이션)
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // 스크롤이 하단 근처에 도달하면 더 로드
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isLoading) {
      onLoadMore();
    }
  };

  const getTabColor = (tab: TabType) => {
    switch (tab) {
      case 'TASK': return colorTokens.orange[500];
      case 'NOTIFICATION': return colorTokens.blue[500];
      case 'READONLY': return colorTokens.purple[500];
    }
  };

  const handleTabClick = (tab: TabType) => {
    onTabChange(tab as NotificationType);
  };

  return (
    <Container>
      <Header $theme={theme}>
        <HeaderTitle>
          <BellIconWrapper>
            <Bell size={20} />
            {totalUnreadCount > 0 && (
              <TotalBadge>{totalUnreadCount > 99 ? '99+' : totalUnreadCount}</TotalBadge>
            )}
          </BellIconWrapper>
          <span>알림</span>
        </HeaderTitle>
        <ReadAllButton onClick={onReadAll}>
          <CheckCheck size={16} />
          모두 읽음
        </ReadAllButton>
      </Header>
      
      <TabContainer>
        {(Object.keys(TAB_CONFIG) as TabType[]).map((tab) => {
          const config = TAB_CONFIG[tab];
          const Icon = config.icon;
          const count = getTabCount(tab);
          const isActive = currentTab === tab;
          
          return (
            <Tab 
              key={tab}
              $isActive={isActive}
              $color={getTabColor(tab)}
              onClick={() => handleTabClick(tab)}
            >
              <TabIcon $isActive={isActive} $color={getTabColor(tab)}>
                <Icon size={16} />
              </TabIcon>
              <TabLabel>{config.label}</TabLabel>
              {count > 0 && (
                <TabBadge $color={getTabColor(tab)} $isActive={isActive}>
                  {count > 99 ? '99+' : count}
                </TabBadge>
              )}
              {isActive && <TabActiveBar $color={getTabColor(tab)} />}
            </Tab>
          );
        })}
      </TabContainer>

      <SearchContainer>
        <SearchIconWrapper>
          <Search size={16} />
        </SearchIconWrapper>
        <SearchInput
          type="text"
          placeholder="키워드를 입력해주세요"
          value={searchKeyword}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </SearchContainer>
      
      <Content ref={contentRef} onScroll={handleScroll}>
        {notifications.length > 0 ? (
          <>
            {notifications.map((notification, index) => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
                onRead={onRead}
                animationDelay={index < 10 ? index * 0.03 : 0}
              />
            ))}
            
            {isLoading && (
              <LoadingContainer>
                <LoadingSpinner>
                  <Loader2 size={24} />
                </LoadingSpinner>
                <LoadingText>불러오는 중...</LoadingText>
              </LoadingContainer>
            )}
            
            {!hasMore && notifications.length > 0 && !isLoading && (
              <EndMessage>
                <CheckCheck size={18} />
                모든 알림을 확인했습니다
              </EndMessage>
            )}
          </>
        ) : isLoading ? (
          <LoadingContainer>
            <LoadingSpinner>
              <Loader2 size={24} />
            </LoadingSpinner>
            <LoadingText>알림을 불러오는 중...</LoadingText>
          </LoadingContainer>
        ) : (
          <EmptyState>
            <EmptyIcon $color={getTabColor(currentTab as TabType)}>
              {searchKeyword ? (
                <Search size={36} />
              ) : (
                <>
                  {currentTab === 'TASK' && <ClipboardList size={36} />}
                  {currentTab === 'NOTIFICATION' && <RefreshCw size={36} />}
                  {currentTab === 'READONLY' && <MessageCircle size={36} />}
                </>
              )}
            </EmptyIcon>
            <EmptyTitle>
              {searchKeyword ? (
                '검색 결과가 없습니다'
              ) : (
                <>
                  {currentTab === 'TASK' && '할일이 없습니다'}
                  {currentTab === 'NOTIFICATION' && '업데이트가 없습니다'}
                  {currentTab === 'READONLY' && '메세지가 없습니다'}
                </>
              )}
            </EmptyTitle>
            <EmptyDescription>
              {searchKeyword ? (
                `"${searchKeyword}"에 대한 검색 결과가 없습니다`
              ) : (
                <>
                  {currentTab === 'TASK' && '처리해야 할 작업이 없습니다'}
                  {currentTab === 'NOTIFICATION' && '새로운 업데이트 소식이 없습니다'}
                  {currentTab === 'READONLY' && '받은 메세지가 없습니다'}
                </>
              )}
            </EmptyDescription>
          </EmptyState>
        )}
      </Content>
    </Container>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const Container = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  width: 480px;
  background: white;
  border-radius: 20px;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 8px 20px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 10000;
  animation: ${fadeIn} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
`;

const getHeaderGradient = (theme: NotificationTheme) => {
  switch (theme) {
    case 'startup':
      return `linear-gradient(135deg, ${colorTokens.green[600]} 0%, ${colorTokens.green[500]} 50%, ${colorTokens.teal[500]} 100%)`;
    case 'investor':
      return `linear-gradient(135deg, ${colorTokens.blue[600]} 0%, ${colorTokens.blue[500]} 50%, ${colorTokens.indigo[500]} 100%)`;
    default:
      return `linear-gradient(135deg, ${colorTokens.green[600]} 0%, ${colorTokens.green[500]} 100%)`;
  }
};

const Header = styled.div<{ $theme: NotificationTheme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  background: ${props => getHeaderGradient(props.$theme)};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.125rem;
  font-weight: 700;
  color: white;
`;

const BellIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TotalBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: ${colorTokens.red[500]};
  border-radius: 9px;
  font-size: 0.625rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const ReadAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: ${colorTokens.gray[100]};
  border-bottom: 1px solid ${colorTokens.gray[200]};
  padding: 0 8px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid ${colorTokens.gray[200]};
`;

const SearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colorTokens.gray[400]};
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  color: ${colorTokens.gray[800]};
  outline: none;
  
  &::placeholder {
    color: ${colorTokens.gray[400]};
  }
`;

const Tab = styled.button<{ $isActive: boolean; $color: string }>`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$isActive ? 'transparent' : colorTokens.gray[200]};
  }
`;

const TabIcon = styled.div<{ $isActive: boolean; $color: string }>`
  display: flex;
  color: ${props => props.$isActive ? props.$color : colorTokens.gray[400]};
  transition: all 0.2s;
`;

const TabLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${colorTokens.gray[900]};
`;

const TabBadge = styled.span<{ $color: string; $isActive: boolean }>`
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: ${props => props.$isActive ? props.$color : colorTokens.gray[300]};
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
`;

const TabActiveBar = styled.div<{ $color: string }>`
  position: absolute;
  bottom: 0;
  left: 12px;
  right: 12px;
  height: 3px;
  background: ${props => props.$color};
  border-radius: 3px 3px 0 0;
`;

const Content = styled.div`
  max-height: 520px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colorTokens.gray[100]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colorTokens.gray[300]};
    border-radius: 4px;
    
    &:hover {
      background: ${colorTokens.gray[400]};
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: ${props => props.$color}15;
  border-radius: 16px;
  color: ${props => props.$color};
  margin-bottom: 14px;
`;

const EmptyTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${colorTokens.gray[900]};
  margin: 0 0 4px 0;
`;

const EmptyDescription = styled.p`
  font-size: 0.8125rem;
  color: ${colorTokens.gray[800]};
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  color: ${colorTokens.blue[500]};
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.div`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${colorTokens.gray[800]};
`;

const EndMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${colorTokens.green[600]};
  background: ${colorTokens.green[100]};
`;

export default NotificationList;
